

import React, { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FaPen, FaSave } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import ContentContainer from '../../../../components/ContentContainer';
import { FormBuilder, FormStateBuilder } from '../../../../components/FormBuilder';
import Loading from '../../../../components/Loading';
import useAlertNotification from '../../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../../services/errorRequest';
import UtilService from '../../../../services/util';
import ValorBeneficioApiService from '../../../../services/api/ValorBeneficioApiService';
import ValorBeneficioValidation from '../../../../services/validation/ValorBeneficioValidation';
import BeneficioValorFormInfo from './BeneficioValorFormInfo';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Form } from 'react-bootstrap';
import FilialApiService from "../../../../services/api/FilialApiService";

const animatedComponents = makeAnimated();

/**
 * Formulário que adiciona os valores de um beneficio em uma filial,
 * criando o relacionamento entre esse beneficio e a filial
 */
function BeneficioValorForm({ dados, onSave, onCancel, editable=false }) {
    // CONTEXT
    const match = useRouteMatch();
    const location = useLocation();
    const history = useHistory();
    const alert = useAlertNotification();
    
    // PROPS
    const { cod } = match.params;

    // STATE
    const [origData, setOrginData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [formFields, setFormFields] = useState(null);
    const [edit, setEdit] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [unidadesSelect, setUnidadesSelect] = useState([]);
    const [unidadesData, setUnidadesData] = useState([]);

    // FUNCTIONS
    const isFormEdited = () => {
        if (!edit.length) return false;
        const jsonOrigData = JSON.stringify(origData);
        const jsonFormData = JSON.stringify(formData);
        return (jsonOrigData !== jsonFormData);
    }
        
    const askForLeave = (cbPositive, cbNegative=()=>{}) => {
        if (cbPositive instanceof Function) {
            const msg = 'Foram feitas alterações!\nDeseja realmente sair e perdê-las?';
            const leave = window.confirm(msg);
            
            if (leave) cbPositive();
            else cbNegative();
        }   
        return false;
    }

    const resetForm = () => UtilService.genericResetFormFunction();
    
    const handleOnCancel = () => {
        if (onCancel) {
            onCancel();
            return;
        }
        history.goBack();
    }

    const hanldeOnSave = () => {
        if (onSave) {
            onSave();
            return;
        } 
        const url = `${match.url.replace(`/${cod}`, '')}`;
        history.replace(url, location.state);
    }

    const saveForm = async () => {
        try {
            // validar form
            const validation = await ValorBeneficioValidation.validate(formData);
            if (!validation.isValid) {
                alert.error('Erros no formulário!');
                return;
            }

            if(unidadesSelect.length === 0) {
                alert.error('Selecione ao menos uma unidade');
                return
            }

            // se ok, enviar p/ API
            const form = {...formData, benefCod: cod, unidades: unidadesSelect.map(u => u.value) };
            const resp = !dados // se novo, ou existente
                ? await ValorBeneficioApiService.createNew(form)
                : await ValorBeneficioApiService.updateOne(form.id, form);

            if (resp.data) {
                setIsEdited(false);
                // setEdit(false);
                alert.success(`Valores do beneficio ${!dados ? 'criados' : 'atualizados'}!`);
                hanldeOnSave();
            }
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
    }

    const handleCancel = () => {
        if (isEdited) {
            askForLeave(() => {
                setIsEdited(false);
                setFormData(origData);
                resetForm();
                handleOnCancel();
            });
            return;
        }
        handleOnCancel();
    }
    
    const onChange = (ev) => {
        const { value, name } = ev.target;
        const newform = {...formData, [name]: value};
        setFormData(newform);
        setEdit([...edit, name]);
    }

    const onEdit = () => {
        if (isFormEdited() && !isEdited) setIsEdited(true);
    }

    const onStart = useCallback(async () => {
        try {
            let [ formCampos, formDados ] = await Promise.all([
                BeneficioValorFormInfo.getFormFields(),
                dados ? {...dados} : null
            ]);
            if (!formDados) {
                formDados = FormStateBuilder(formCampos);
            }
            setFormFields( formCampos );
            setFormData( formDados );
            setOrginData( formDados );
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
        finally {
            setIsLoading(false);
        }
    }, []);

    // USEEFFECTS
    useEffect(onStart, []);
    useEffect(onEdit, [formData]);

    const actions = [
        { 
            label: 'Cancelar',
            icon: <MdClose /> ,
            color: 'danger',
            type: 'button',
            onClick: handleCancel
        }
    ]

    if (editable) {
        actions.unshift(
            { 
                label: dados ? 'Confirmar edição' : 'Salvar',
                icon: dados ? <FaPen /> : <FaSave />,
                color: dados ? 'info' : 'success',
                type: 'button',
                onClick: saveForm
            }
        );
    }

    const title = `Valor de Custo Adicional`;

    const handleGetUnidades = async () => {
        try{
            const filiaisData = await (FilialApiService.getAll()).then(resp => resp.data);
            
            setUnidadesData(
                filiaisData.data.map(item => ({
                    value: item.cod,
                    label: item.label
                }))
            );
            setUnidadesSelect(
                filiaisData.data.filter((u) => u.cod == dados?.filialCod).map(item => ({
                    value: item.cod,
                    label: item.label
                }))
            )
        } catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors();
            alert.error(errorMsg);
        }
    }

    const handleUnidadeChange = (selectedOption) => {
        setUnidadesSelect(selectedOption);
    };

    useEffect(() => {
        handleGetUnidades();
    }, []);

    return (
        <ContentContainer
            title={title}
            actions={actions}
        >
            { isLoading 
                ? <Loading message="Carregando..." />
                : ( origData && 
                    <>
                        <Form.Group>
                            <Form.Label>Selecione as unidades:</Form.Label>
                            <Select
                                components={animatedComponents}
                                isMulti={dados ? false : true}
                                options={unidadesData}
                                value={unidadesSelect}
                                onChange={(e) => {handleUnidadeChange(e)}} 
                                placeholder="Selecione as unidades"
                                closeMenuOnSelect={true}
                                isDisabled={dados}
                            />
                        </Form.Group>
                        <FormBuilder 
                            formFields={formFields}
                            formData={formData}
                            onChange={onChange}
                            disabledForm={!editable}
                            initialValues={origData}
                            schema={ValorBeneficioValidation.schema}
                        />
                    </>                 
                )
            }
            
            <Prompt
                when={isEdited}
                message={location => askForLeave(() => {
                    setIsEdited(false);
                    setTimeout(() => {
                        history.push(location.pathname);
                    }, 100);
                })}
            />
        </ContentContainer>
    )
}

export default BeneficioValorForm;
